import React from 'react';
import moment from 'moment';
import { getRoles } from '../services/userRights';
import { BarChart, PieChart, Cell, Bar, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiGet, apiPost } from '../services/apiClient';
import Forbidden from './forbidden/forbidden.jsx';
import DateTimeInput from '../controls/dateTimeInput.jsx';

class FinanceToolTip extends React.Component {
    render() {
        const { active, payload, label } = this.props;
        if (!active || !payload) {
            return null;
        }
        return (
            <div className="recharts-default-tooltip"
                style={{ margin: '0px', padding: '10px', backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
            >
                <p className="recharts-tooltip-label" style={{ margin: '0px' }}>{label}</p>
                <ul className="recharts-tooltip-item-list" style={{ padding: '0px', margin: '0px' }}>
                    {payload.map((item, i) => (
                        <li key={i}
                            className="recharts-tooltip-item"
                            style={{ display: 'block', paddingTop: '4px', paddingBottom: '4px', color: item.color }}
                >
                            <span className="recharts-tooltip-item-name">{item.name}</span>
                            <span className="recharts-tooltip-item-separator"> : </span>
                            <span className="recharts-tooltip-item-value">{(item.value / 1000).toFixed(0)}</span>
                            <span className="recharts-tooltip-item-unit">K ₽</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
}

class DecimalToolTip extends React.Component {
    render() {
        const { active, payload, label } = this.props;
        if (!active || !payload) {
            return null;
        }
        return (
            <div className="recharts-default-tooltip"
                style={{ margin: '0px', padding: '10px', backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
            >
                <p className="recharts-tooltip-label" style={{ margin: '0px' }}>{label}</p>
                <ul className="recharts-tooltip-item-list" style={{ padding: '0px', margin: '0px' }}>
                    {payload.map((item, i) => (
                        <li key={i}
                            className="recharts-tooltip-item"
                            style={{ display: 'block', paddingTop: '4px', paddingBottom: '4px', color: item.color }}
                        >
                            <span className="recharts-tooltip-item-name">{item.name}</span>
                            <span className="recharts-tooltip-item-separator"> : </span>
                            <span className="recharts-tooltip-item-value">{item.value.toFixed(2)}</span>
                            <span className="recharts-tooltip-item-unit"></span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
}

export default class StatisticsPage extends React.Component {
    constructor(args) {
        super(args);

        this.state = {
            colors: ["#5899DA", "#E8743B", "#19A979", "#ED4A7B", "#945ECF", "#13A4B4", "#525DF4", "#BF399E", "#6C8893", "#EE6868", "#2F6497"],
            carriers: {},
            parcelStatues: [],
            finance: [],
            clients: [],
            newParcels: 0,
            activeUsers: 0,
            sendErrors: 0,
            shippingLateness: {},
            deliveryLateness: {},
            directions: {},
            activeDirectionsTab: 'year',
            activeLatenessTab: 'delivery',
            fromDate: moment().add(-1, 'years'),
            toDate: moment()
        }
    }

    componentDidMount() {
        this.loadData();
    }

    loadData() {
        let request = {
            fromDate: this.getDateTimeStr(this.state.fromDate),
            toDate: this.getDateTimeStr(this.state.toDate)
        };

        apiPost('inner/stats/carriers', request)
            .then(data => this.setState({ carriers: data }));
        apiPost('inner/stats/parcels/status', request)
            .then(data => this.setState({ parcelStatues: data }));
        apiPost('inner/stats/finance', request)
            .then(data => this.setState({ finance: data }));
        apiPost('inner/stats/clients', request)
            .then(data => this.setState({ clients: data }));
        apiGet('inner/stats/parcels/new')
            .then(data => this.setState({ newParcels: data }));
        apiGet('inner/stats/users/active')
            .then(data => this.setState({ activeUsers: data }));
        apiPost('inner/stats/lateness', request)
            .then(data => this.setState({ shippingLateness: data ? data.shippingData : null, deliveryLateness: data ? data.deliveryData : null }));
        apiGet('inner/stats/parcels/errors')
            .then(data => this.setState({ sendErrors: data }));
        apiPost('inner/stats/directions', request)
            .then(data => this.setState({ directions: data }));
    }

    getDateTimeStr(value) {
        if (value) {
            return moment(value).format('YYYY-MM-DDTHH:mm:ss');
        } else {
            return null;
        }
    }

    renderCarriersChart(data) {
        if (!data || !data.carriers || data.carriers.length < 1) {
            return null;
        }
        let bars = data.carriers.slice(1).map((value, ind) => {
            return (
                <Bar key={value.code}
                    name={value.name}
                    dataKey={value.code}
                    fill={this.state.colors[(ind + 1) % this.state.colors.length]}
                />
            );
        });
        return (
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={data.entries}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar key={data.carriers[0].code}
                        name={data.carriers[0].name}
                        dataKey={data.carriers[0].code}
                        fill={this.state.colors[0]}
                    />
                    {bars}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderCarrierLatenessChart(data) {
        if (!data || !data.carriers || data.carriers.length < 1) {
            return null;
        }
        let bars = data.carriers.slice(1).map((value, ind) => {
            return (
                <Bar key={value.code}
                    name={value.name}
                    dataKey={value.code}
                    fill={this.state.colors[(ind + 1) % this.state.colors.length]}
                />
            );
        });
        return (
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={data.entries}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip content={<DecimalToolTip/>} />
                    <Legend />
                    <Bar key={data.carriers[0].code}
                        name={data.carriers[0].name}
                        dataKey={data.carriers[0].code}
                        fill={this.state.colors[0]}
                    />
                    {bars}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderParcelStatusesChart(data) {
        if (!data) {
            return null;
        }
        return (
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar name="Создана"
                        dataKey="created"
                        stackId="a"
                        fill={this.state.colors[0]}
                    />
                    <Bar name="Отправлена"
                        dataKey="shipped"
                        stackId="a"
                        fill={this.state.colors[1]}
                    />
                    <Bar name="Доставлена"
                        dataKey="delivered"
                        stackId="a"
                        fill={this.state.colors[2]}
                    />
                    <Bar name="Проблема"
                        dataKey="problems"
                        stackId="a"
                        fill={this.state.colors[3]}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderFinanceChart(data) {
        if (!data) {
            return null;
        }
        return (
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={tick => { return (tick / 1000).toFixed(0) + 'K ₽'; }} />
                    <Tooltip content={<FinanceToolTip />} />
                    <Legend />
                    <Bar name="Себестоимость"
                        dataKey="rawCost"
                        stackId="a"
                        fill={this.state.colors[0]}
                    />
                    <Bar name="Надбавка"
                        dataKey="addedCost"
                        stackId="a"
                        fill={this.state.colors[1]}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderClientsChart(data) {
        if (!data) {
            return null;
        }
        return (
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar name="Количество клиентов"
                        dataKey="count"
                        stackId="a"
                        fill={this.state.colors[5]}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderDirectionsChart(data) {
        if (!data) {
            return null;
        }
        return (
            <ResponsiveContainer width='100%' height={300}>
                <PieChart>
                    <Pie data={data} dataKey="value">
                        <Cell fill={this.state.colors[0]} />
                        <Cell fill={this.state.colors[1]} />
                        <Cell fill={this.state.colors[2]} />
                        <Cell fill={this.state.colors[3]} />
                    </Pie>
                    <Tooltip />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        );
    }

    render() {
        let roles = getRoles();
        let hasAccess = roles.includes('Admin') || roles.includes('GlobalAdmin') || roles.includes('Financial');
        if (!hasAccess) {
            return <Forbidden />;
        }
        return (
            <section className="content">
                <div className="container">
                    <div className="row">
                        <div className="col-xs-12 col-md-4">
                            <div className="small-box bg-aqua">
                                <div className="inner">
                                    <h3>{this.state.activeUsers}</h3>
                                    <p>Активных пользователей</p>
                                </div>
                                <div className="icon">
                                    <i className="fa fa-users"></i>
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-4">
                            <div className="small-box bg-green">
                                <div className="inner">
                                    <h3>{this.state.newParcels}</h3>
                                    <p>Новых посылок за последний час</p>
                                </div>
                                <div className="icon">
                                    <i className="fa fa-paper-plane"></i>
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-4">
                            <div className="small-box bg-red">
                                <div className="inner">
                                    <h3>{this.state.sendErrors}</h3>
                                    <p>Ошибок отправки за последний час</p>
                                </div>
                                <div className="icon">
                                    <i className="fa fa-warning"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12">
                            <div className="box">
                                <div className="box-body">
                                    <form className="form-inline">
                                        <label htmlFor="inputFromDate" className="inline-wide">Дата от</label>
                                        <DateTimeInput id="inputFromDate"
                                            enableTime={false}
                                            onChange={value => this.setState({ fromDate: value })}
                                            onEditComplete={() => this.loadData()}
                                            value={this.state.fromDate}
                                        />
                                        <label htmlFor="inputToDate" className="inline-wide">до</label>
                                        <DateTimeInput id="inputToDate"
                                            enableTime={false}
                                            onChange={value => this.setState({ toDate: value })}
                                            onEditComplete={() => this.loadData()}
                                            value={this.state.toDate}
                                        />
                                        <button type="button"
                                            className="btn btn-default btn-sm"
                                            style={{ marginLeft: '10px' }}
                                            onClick={() => this.loadData()}>
                                            Обновить
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="box box-primary">
                                <div className="box-header">
                                    <i className="fa fa-bar-chart"></i>
                                    <h3 className="box-title">Транспортные компании</h3>
                                </div>
                                <div className="box-body">
                                    {this.renderCarriersChart(this.state.carriers)}
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="box box-primary">
                                <div className="box-header">
                                    <i className="fa fa-bar-chart"></i>
                                    <h3 className="box-title">Активные клиенты</h3>
                                </div>
                                <div className="box-body">
                                    {this.renderClientsChart(this.state.clients)}
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="box box-primary">
                                <div className="box-header">
                                    <i className="fa fa-bar-chart"></i>
                                    <h3 className="box-title">Статус посылок</h3>
                                </div>
                                <div className="box-body">
                                    {this.renderParcelStatusesChart(this.state.parcelStatues)}
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="box box-primary">
                                <div className="box-header">
                                    <i className="fa fa-bar-chart"></i>
                                    <h3 className="box-title">Объем перевозок, тыс. руб.</h3>
                                </div>
                                <div className="box-body">
                                    {this.renderFinanceChart(this.state.finance)}
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="nav-tabs-custom">
                                <ul className="nav nav-tabs pull-right ui-sortable-handle">
                                    <li className={this.state.activeLatenessTab == 'delivery' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                                        <a onClick={() => this.setState({ activeLatenessTab: 'delivery' })}>на доставку</a>
                                    </li>
                                    <li className={this.state.activeLatenessTab == 'shipping' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                                        <a onClick={() => this.setState({ activeLatenessTab: 'shipping' })}>на отгрузку</a>
                                    </li>
                                    <li className="pull-left box-header">
                                        <i className="fa fa-bar-chart"></i>
                                        <h3 className="box-title">Опоздание</h3>
                                    </li>
                                </ul>
                                <div className="tab-content chart-tab">
                                    <div className={'tab-pane' + (this.state.activeLatenessTab == 'shipping' ? ' active' : '')}>
                                        {this.renderCarrierLatenessChart(this.state.shippingLateness)}
                                    </div>
                                    <div className={'tab-pane' + (this.state.activeLatenessTab == 'delivery' ? ' active' : '')}>
                                        {this.renderCarrierLatenessChart(this.state.deliveryLateness)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-4">
                            <div className="nav-tabs-custom">
                                <ul className="nav nav-tabs pull-right ui-sortable-handle">
                                    <li className={this.state.activeDirectionsTab == 'year' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                                        <a onClick={() => this.setState({ activeDirectionsTab: 'year' })}>за год</a>
                                    </li>
                                    <li className={this.state.activeDirectionsTab == 'month' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                                        <a onClick={() => this.setState({ activeDirectionsTab: 'month' })}>за месяц</a>
                                    </li>
                                    <li className="pull-left box-header">
                                        <i className="fa fa-bar-chart"></i>
                                        <h3 className="box-title">Направления</h3>
                                    </li>
                                </ul>
                                <div className="tab-content chart-tab">
                                    <div className={'tab-pane' + (this.state.activeDirectionsTab == 'year' ? ' active' : '')}>
                                        {this.renderDirectionsChart(this.state.directions ? this.state.directions.yearData : null)}
                                    </div>
                                    <div className={'tab-pane' + (this.state.activeDirectionsTab == 'month' ? ' active' : '')}>
                                        {this.renderDirectionsChart(this.state.directions ? this.state.directions.monthData : null)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}


// WEBPACK FOOTER //
// ./App/pages/statistics.jsx